import { Anchor, Button, Group, Modal, Select, Stack, Table, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { type InboxFile, useAdminMutation, useAdminQuery } from "../../api/admin";
import { api } from "../../api/client";
import { useCompanyOptions, useNames } from "../../api/hooks";
import { formatBytes, formatDateTime } from "../../lib/format";

export function InboxPage() {
  const { t } = useTranslation();
  const { lookups, docType } = useNames();
  const files = useAdminQuery<{ items: InboxFile[] }>("inbox", "/api/admin/inbox", 15_000);
  const [target, setTarget] = useState<InboxFile | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const companyOptions = useCompanyOptions(companyId);
  const [docTypeId, setDocTypeId] = useState<string | null>(null);
  const assign = useAdminMutation(
    (v: { id: number; companyId: number; docTypeId?: number }) =>
      api<{ status: string; documentId?: number; error?: string }>(
        `/api/admin/inbox/${v.id}/assign`,
        {
          method: "POST",
          body: { companyId: v.companyId, ...(v.docTypeId ? { docTypeId: v.docTypeId } : {}) },
        },
      ),
    ["inbox", "ingestion"],
  );

  const items = files.data?.items ?? [];
  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        {t("admin.inbox.hint")}
      </Text>
      {items.length === 0 && <Text c="dimmed">{t("admin.inbox.empty")}</Text>}
      {items.length > 0 && (
        <Table.ScrollContainer minWidth={640}>
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("admin.inbox.path")}</Table.Th>
                <Table.Th>{t("admin.inbox.size")}</Table.Th>
                <Table.Th>{t("upload.state")}</Table.Th>
                <Table.Th>{t("admin.inbox.seen")}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((f) => (
                <Table.Tr key={f.id}>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {f.sourceName ?? t("sources.inbox")}
                    </Text>
                    <Text size="sm" style={{ wordBreak: "break-all" }}>
                      {f.relPath}
                    </Text>
                    {f.error && (
                      <Text size="xs" c="orange">
                        {f.error}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{formatBytes(f.sizeBytes)}</Table.Td>
                  <Table.Td>
                    {t(`admin.inbox.status.${f.status}`, f.status)}
                    {f.documentId && (
                      <>
                        {" "}
                        <Anchor component={Link} to={`/documents/${f.documentId}`} size="sm">
                          #{f.documentId}
                        </Anchor>
                      </>
                    )}
                  </Table.Td>
                  <Table.Td>{formatDateTime(f.firstSeenAt)}</Table.Td>
                  <Table.Td>
                    <Button
                      size="compact-xs"
                      onClick={() => {
                        setTarget(f);
                        setCompanyId(null);
                        setDocTypeId(null);
                      }}
                    >
                      {t("admin.inbox.assign")}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Modal
        opened={target !== null}
        onClose={() => setTarget(null)}
        title={t("admin.inbox.assignTitle")}
      >
        <Stack>
          <Text size="sm">{target?.relPath}</Text>
          <Select
            label={t("document.company")}
            data={companyOptions}
            value={companyId}
            onChange={setCompanyId}
            required
          />
          <Select
            label={t("document.type")}
            data={(lookups.data?.docTypes ?? []).map((d) => ({
              value: String(d.id),
              label: docType(d.id),
            }))}
            value={docTypeId}
            onChange={setDocTypeId}
            clearable
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={!companyId}
              loading={assign.isPending}
              onClick={async () => {
                if (!target || !companyId) return;
                try {
                  const r = await assign.mutateAsync({
                    id: target.id,
                    companyId: Number(companyId),
                    ...(docTypeId ? { docTypeId: Number(docTypeId) } : {}),
                  });
                  notifications.show({
                    message: `${t(`admin.inbox.status.${r.status}`, r.status)}${r.documentId ? ` #${r.documentId}` : ""}${r.error ? `: ${r.error}` : ""}`,
                    color: r.status === "ingested" ? "green" : "orange",
                  });
                  setTarget(null);
                } catch (e) {
                  notifications.show({ message: (e as Error).message, color: "red" });
                }
              }}
            >
              {t("admin.inbox.assign")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
